import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import styled from "styled-components";
import Select from "react-select";

const CLOUDINARY_CLOUD_NAME = "dfjaij1fb";
const CLOUDINARY_UPLOAD_PRESET = "premium_perfume";

const seasonsOptions = [
  { value: "Spring", label: "Spring" },
  { value: "Summer", label: "Summer" },
  { value: "Fall", label: "Fall" },
  { value: "Winter", label: "Winter" },
];

const customStyles = {
  control: (provided) => ({
    ...provided,
    backgroundColor: "#1a1a1a",
    borderColor: "#ddd",
    minWidth: "160px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#303030",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "grey" : "#303030",
    color: "white",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "grey",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "white",
  }),
};

const ProductsTable = () => {
  const [products, setProducts] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [editProduct, setEditProduct] = useState({
    name: "",
    image: "",
    category: "",
    seasons: [],
    showInSlider: false,
    inStock: true,
  });

  useEffect(() => {
    const fetchProducts = async () => {
      const querySnapshot = await getDocs(collection(db, "products"));
      const productsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsList);
    };
    fetchProducts();
  }, []);

  const handleEditClick = (product) => {
    setEditingProductId(product.id);
    // Convert seasons array to react-select format
    const seasonsValue = Array.isArray(product.seasons)
      ? product.seasons.map((s) => ({ value: s, label: s }))
      : [];
    setEditProduct({
      ...product,
      seasons: seasonsValue,
      showInSlider: product.showInSlider || false,
      inStock: product.inStock !== undefined ? product.inStock : true,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setEditProduct({ ...editProduct, [name]: checked });
    } else {
      setEditProduct({ ...editProduct, [name]: value });
    }
  };

  const handleSeasonsChange = (selectedOptions) => {
    if (selectedOptions.length <= 4) {
      setEditProduct({ ...editProduct, seasons: selectedOptions });
    }
  };

  const handleSaveClick = async () => {
    const productDoc = doc(db, "products", editingProductId);

    let imageUrl = editProduct.image;
    if (editImageFile) {
      const formData = new FormData();
      formData.append("file", editImageFile);
      formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: "POST", body: formData }
      );
      const data = await response.json();
      imageUrl = data.secure_url;
    }

    const dataToSave = {
      ...editProduct,
      image: imageUrl,
      seasons: editProduct.seasons.map((s) => s.value),
    };
    await updateDoc(productDoc, dataToSave);
    setEditingProductId(null);
    setEditImageFile(null);
    setProducts(
      products.map((product) =>
        product.id === editingProductId ? dataToSave : product
      )
    );
  };

  const handleDeleteClick = async (id) => {
    await deleteDoc(doc(db, "products", id));
    setProducts(products.filter((product) => product.id !== id));
  };

  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            <th>Image</th>
            <th>Name</th>
            <th>Category</th>
            <th>Seasons</th>
            <th>In Stock</th>
            <th>Show in Slider</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product.id}>
              {editingProductId === product.id ? (
                <>
                  <td>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setEditImageFile(e.target.files[0])}
                    />
                    {!editImageFile && editProduct.image && (
                      <img src={editProduct.image} alt="" className="product-image" />
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      name="name"
                      value={editProduct.name}
                      onChange={handleInputChange}
                    />
                  </td>
                  <td>
                    <select
                      name="category"
                      value={editProduct.category}
                      onChange={handleInputChange}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Unisex">Unisex</option>
                    </select>
                  </td>
                  <td>
                    <Select
                      isMulti
                      options={seasonsOptions}
                      value={editProduct.seasons}
                      onChange={handleSeasonsChange}
                      closeMenuOnSelect={false}
                      isOptionDisabled={() => editProduct.seasons.length >= 4}
                      styles={customStyles}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={editProduct.inStock}
                      onChange={handleInputChange}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      name="showInSlider"
                      checked={editProduct.showInSlider}
                      onChange={handleInputChange}
                    />
                  </td>
                  <td>
                    <button onClick={handleSaveClick} className="saveBtn">
                      Save
                    </button>
                    <button
                      onClick={() => handleDeleteClick(product.id)}
                      className="deleteBtn"
                    >
                      Delete
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td>
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="product-image"
                      />
                    ) : (
                      "No image"
                    )}
                  </td>
                  <td>{product.name}</td>
                  <td>{product.category}</td>
                  <td>
                    {Array.isArray(product.seasons)
                      ? product.seasons.join(", ")
                      : product.seasons}
                  </td>
                  <td>{product.inStock !== false ? "Yes" : "No"}</td>
                  <td>{product.showInSlider ? "Yes" : "No"}</td>
                  <td>
                    <button onClick={() => handleEditClick(product)}>
                      Edit
                    </button>
                    <button
                      className="deleteBtn"
                      onClick={() => handleDeleteClick(product.id)}
                    >
                      Delete
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

const TableContainer = styled.div`
  margin: 20px;
  overflow-x: auto;
  width: 100%;
  margin-top: 5em;

  .product-image {
    height: 60px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    .product-image {
      height: 40px;
    }
    table {
      display: block;
      width: 100%;
      overflow-x: auto;
      white-space: nowrap;
    }
    thead {
      display: none;
    }
    tbody {
      display: block;
    }
    tr {
      display: table;
      width: 100%;
      table-layout: fixed;
      border-bottom: 1px solid #ddd;
      margin-bottom: 10px;
    }
    td {
      display: block;
      text-align: right;
      padding: 10px;
      border: none;
      position: relative;
      ::before {
        content: attr(data-label);
        position: absolute;
        left: 0;
        width: 50%;
        padding: 0 10px;
        font-weight: bold;
        white-space: nowrap;
        text-align: left;
      }
    }
  }
`;

const Table = styled.table`
  width: 98%;
  border-collapse: collapse;

  th {
    background-color: #333333;
    color: white;
    padding: 10px;
    text-align: left;
    border-radius: 10px 10px 0 0;
  }

  td {
    padding: 10px;
    border: 1px solid #ddd;
    .deleteBtn {
      background: #d87474;
    }
  }

  button {
    background-color: #4caf50;
    border: none;
    color: white;
    padding: 10px 20px;
    text-align: center;
    text-decoration: none;
    display: inline-block;
    font-size: 16px;
    margin: 4px 2px;
    cursor: pointer;
    border-radius: 4px;
    margin-right: 5px;
  }

  input {
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #ddd;
    width: 100%;
  }

  select {
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #ddd;
    width: 100%;
    background: #1a1a1a;
    color: white;
  }

  img {
    max-width: 100px;
    height: auto;
  }
`;

export default ProductsTable;
